import React, { useEffect, useState } from 'react'
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromRaw,
  convertFromHTML,
  ContentBlock,
  CompositeDecorator,
  getDefaultKeyBinding,
  KeyBindingUtil,
  DraftHandleValue,
  Modifier,
} from 'draft-js'
import { SyntheticKeyboardEvent } from 'react-draft-wysiwyg'
import moment from 'moment'

// NOTE: Utility Functions

const findWithRegex = (
  regex: RegExp,
  contentBlock: ContentBlock,
  callback: any
) => {
  const text = contentBlock.getText()
  let matchArr, start
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index
    callback(start, start + matchArr[0].length)
  }
}

// NOTE: Placeholder Logic

export const TextEditorPlaceholders = [
  {
    label: 'First Name',
    type: ['event', 'membership'],
    value: '{{firstname}} ',
    dummyData: 'John',
  },
  {
    label: 'Last Name',
    type: ['event', 'membership'],
    value: '{{lastname}} ',
    dummyData: 'Smith',
  },
  {
    label: 'Event Name',
    type: ['event'],
    value: '{{title}} ',
    dummyData: 'Forumm Event',
    useRealDataIfAvailable: true,
  },
  {
    label: 'Membership Name',
    type: ['membership'],
    value: '{{title}} ',
    dummyData: 'Forumm Membership',
    useRealDataIfAvailable: true,
  },
  {
    label: 'Event Link',
    type: ['event'],
    value: '{{eventlink}} ',
    dummyData: '<a href="www.forumm.to">Event Page</a>',
    useRealDataIfAvailable: true,
  },
  // {
  //   label: 'Recipient Email',
  // type: ['event'],
  //   value: '{{email}} ',
  //   dummyData: 'j.smith@forumm.to',
  // },
  {
    label: 'Start Date & Time',
    type: ['event'],
    value: '{{startdatetime}} ',
    dummyData: moment().format('D MMMM YYYY, h:mm A'),
    useRealDataIfAvailable: true,
  },
  {
    label: 'Location',
    type: ['event'],
    value: '{{eventlocation}} ',
    dummyData: 'Glasgow',
    useRealDataIfAvailable: true,
  },
  {
    label: 'QR Code',
    type: ['event'],
    value: '{{qr}} ',
    dummyData:
      '<img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=dummycode" alt="QR Code" />',
  },
  {
    label: 'Number of Tickets Purchased',
    type: ['event'],
    value: '{{numberoftickets}} ',
    dummyData: '2',
  },
  {
    label: 'Ticket Title',
    type: ['event'],
    value: '{{tickettitle}} ',
    dummyData: 'General Admission',
  },
  // Add more as needed, e.g., { label: 'Company', value: '{{company}}', dummyData: 'Forumm' }
]

export const DEFAULT_EDITOR_FONT_FAMILY = 'Poppins'

const getFontFamilyLabel = (fontFamily?: string) => {
  return fontFamily || 'Font'
}

const HEADING_FONT_SIZES: Record<string, number> = {
  'header-one': 40,
  'header-two': 32,
  'header-three': 24,
}

export const FontSizeDropdown = ({
  onChange,
  expanded,
  onExpandEvent,
  doCollapse,
  currentState,
  config,
  editorState,
}: any) => {
  const blockType = editorState
    ?.getCurrentContent()
    .getBlockForKey(editorState.getSelection().getStartKey())
    ?.getType()
  const selectedFontSize =
    HEADING_FONT_SIZES[blockType] || currentState?.fontSize

  return (
    <div
      className="rdw-fontsize-wrapper rdw-dropdown-wrapper"
      aria-label="rdw-font-size-control"
      title="Font size"
      onClick={onExpandEvent}
    >
      <span className="rdw-dropdown-selectedtext">
        {selectedFontSize ? `${selectedFontSize}px` : 'Size'}
      </span>
      <span
        className={
          expanded ? 'rdw-dropdown-carettoclose' : 'rdw-dropdown-carettoopen'
        }
      />
      {expanded ? (
        <div
          className="rdw-dropdown-optionwrapper rdw-fontsize-optionwrapper"
          onClick={(event) => event.stopPropagation()}
        >
          {config.options.map((option: number) => (
            <div
              key={option}
              className="rdw-dropdownoption-default rdw-fontsize-option"
              data-active={selectedFontSize === option}
              onClick={() => {
                onChange(option)
                doCollapse?.()
              }}
            >
              {option}px
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export const FontFamilyDropdown = ({
  onChange,
  expanded,
  onExpandEvent,
  doCollapse,
  currentState,
  config,
}: any) => {
  const selectedFontFamily = currentState?.fontFamily

  return (
    <div
      className="rdw-fontfamily-wrapper rdw-dropdown-wrapper w-12"
      aria-label="rdw-font-family-control"
      title="Font"
      onClick={onExpandEvent}
    >
      <span className="rdw-dropdown-selectedtext min-w-11">
        {getFontFamilyLabel(selectedFontFamily)}
      </span>
      <span
        className={
          expanded ? 'rdw-dropdown-carettoclose' : 'rdw-dropdown-carettoopen ml-1'
        }
      />
      {expanded ? (
        <div
          className="rdw-dropdown-optionwrapper rdw-fontfamily-optionwrapper"
          onClick={(event) => event.stopPropagation()}
        >
          {config.options.map((option: string) => (
            <div
              key={option}
              className="rdw-dropdownoption-default rdw-fontfamily-option"
              data-active={
                selectedFontFamily === option
              }
              onClick={() => {
                onChange(option)
                doCollapse?.()
              }}
            >
              {getFontFamilyLabel(option)}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export const PLACEHOLDER_REGEX = (type: string = 'event') =>
  new RegExp(
    `\{\{(${TextEditorPlaceholders.filter((f) => f.type.includes(type))
      .map((p) => p.value.trim().slice(2, -2))
      .join('|')})\}\}`,
    'g'
  )

const Placeholder = (props: any) => {
  return (
    <span
      spellCheck={false}
      style={{
        padding: '2px 2px',
        borderRadius: '4px',
        backgroundColor: '#fff7cc',
        border: '1px dashed #e0b200',
        fontFamily: 'monospace',
      }}
    >
      {props.children}
    </span>
  )
}

export const PlaceholderButton = ({
  editorState,
  onChange,
  type = 'event',
}: {
  editorState: EditorState
  onChange: any
  type?: string
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const insertPlaceholder = (value: string) => {
    const currentContent = editorState.getCurrentContent()
    const selection = editorState.getSelection()
    const newContent = Modifier.insertText(currentContent, selection, value)
    const newEditorState = EditorState.push(
      editorState,
      newContent,
      'insert-characters'
    )
    onChange(newEditorState) // Update the editor state
    setIsMenuOpen(false) // Close menu
  }

  return (
    <div style={{ position: 'relative' }}>
      <div
        className="rdw-option-wrapper"
        style={{ border: 'none', color: '#3333ee' }}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        title="Insert Dynamic Data"
      >
        {/* You can use text, an icon (e.g., from react-icons), or emoji */}
        Personalise
      </div>
      {isMenuOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            zIndex: 1000,
            minWidth: '150px',
          }}
        >
          {TextEditorPlaceholders.filter((f) => f.type.includes(type)).map(
            (item, index) => (
              <div
                key={index}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom:
                    index < TextEditorPlaceholders.length - 1
                      ? '1px solid #eee'
                      : 'none',
                }}
                onClick={() => insertPlaceholder(item.value)}
              >
                {item.label}
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

const placeholderStrategyFull = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) => {
  findWithRegex(PLACEHOLDER_REGEX('event'), contentBlock, callback)
}

const placeholderStrategyMembership = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) => {
  findWithRegex(PLACEHOLDER_REGEX('membership'), contentBlock, callback)
}

// NOTE: Outputs

export const EditorFullCustomDecorators = [
  {
    strategy: placeholderStrategyFull,
    component: Placeholder,
  },
]

export const EditorMembershipCustomDecorators = [
  {
    strategy: placeholderStrategyMembership,
    component: Placeholder,
  },
]

// NOTE: Unused/WIP

// export const keyBindingFn = (e: SyntheticKeyboardEvent): string | null => {
//   console.log({ key: e.key })
//   if (e.key === ' ' && KeyBindingUtil.isCtrlKeyCommand(e))
//     return 'init-autocomplete'
//
//   return getDefaultKeyBinding(e)
// }

// export const CustomMention = (props: any) => {
//   // props.children is the mention text
//   console.log({ props })
//   return <span className="custom-mention">{props.children}</span>
// }
